import React, { useEffect, useState } from "react";

// Material component
import { Button, Card, Divider, Grid, FormControl } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Custom Component
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageTitle from "examples/NewDesign/PageTitle";
import DeleteModal from "examples/modal/deleteModal/deleteModal";
import NewShift from "examples/modal/shift";
import CustomFilterDropdown from "components/Dropdown/CustomFilterDropdown";
import CustomAutoComplete from "components/Dropdown/CustomAutoComeplete";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { projectListThunk, dalShitFiltersThunk, exportAllShiftPdf } from "redux/Thunks/Filter";
import { deleteShiftThunk } from "redux/Thunks/DalShift";
import { openSnackbar } from "redux/Slice/Notification";
import configThunk from "redux/Thunks/Config";

// Data
import useDailyShiftData from "layouts/dalShiftDetails/data/dailyShiftData";
import DataTable from "examples/Tables/DataTable";
import CustomButton from "examples/NewDesign/CustomButton";
import BasicButton from "examples/NewDesign/CustomButton/BasicButton";

// Feature Flag
import { Feature } from "flagged";

// Constant
import Constants, {
  Icons,
  ModalContent,
  PageTitles,
  ButtonTitles,
  Colors,
  FeatureTags,
  defaultData,
  FiltersModuleName,
  Common,
} from "utils/Constants";
import pxToRem from "assets/theme/functions/pxToRem";
import UserListThunk from "redux/Thunks/UserManagement";
import { reloadData, updateCompleted, removeShift } from "redux/Slice/DalShift";
import { setStoreFilters, resetFilters } from "redux/Slice/Filter";
import FontComponent from "components/Responsive/fonts";
import { findPageNumberAfterRecordDelete, paramCreater } from "utils/methods/methods";
import { createSocketClient, disconnectSocket } from "utils/socket/socketClient";
import {
  fetchBytesFromUrl,
  fetchExportStatusPayload,
  EXCEL_STATUS_POLL_INTERVAL_MS,
  pollExportJobStatusUntilReady,
  triggerFileDownload,
} from "utils/pdfExport";

const EXCEL_SOCKET_TIMEOUT_MS = 300000;
const EXCEL_CONTENT_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const shiftsFilterArr = [
  {
    inputLabel: FiltersModuleName.PROJECT,
    list: [FiltersModuleName.SHIFT_DETAILS_FILTERS_TITLE_OBJ],
    selectedValue: FiltersModuleName.ALL_IN_SMALL_CASE,
  },
  {
    inputLabel: FiltersModuleName.CREATED,
    list: FiltersModuleName.COMMON_CUSTOM_DATE_FILTER_OPTIONS,
    selectedValue: FiltersModuleName.ALL_WITH_FIRST_LETTER_CAPITAL,
    fromDate: "",
    toDate: "",
  },
  {
    inputLabel: FiltersModuleName.STATUS,
    list: FiltersModuleName.SHIFT_DETAILS_STATUS_FILTER_OPTIONS,
    selectedValue: FiltersModuleName.ALL_WITH_FIRST_LETTER_CAPITAL,
  },
];

function DalShiftDetails() {
  const mongooseId = "_id";
  const dispatch = useDispatch();

  const shiftData = useSelector((state) => state?.dalShift);
  const shiftStoredFilters = useSelector((state) => state.filtersSlice.shift);
  const ConfigData = useSelector((state) => state.config);
  const permission = ConfigData?.screens?.[5]?.screensInfo?.agreement;

  const initialFilters = shiftStoredFilters?.length > 0 ? shiftStoredFilters : shiftsFilterArr;

  const [shouldUpdateState, setShouldUpdateState] = useState(false);
  const [openDeleteModal, setDeleteModal] = useState(false);
  const [openNewShift, setOpenNewShift] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const [selectedId, setSelectedId] = useState("");
  const [activeUsers, setActiveUsers] = useState([]);
  const [exportLoader, setExportLoader] = useState(false);

  const [tablePagination, setTablePagination] = useState({
    page: 0,
    perPage: defaultData.PER_PAGE_5,
  });

  const fontSize = FontComponent({
    sizes: {},
  });

  const handleCloseDeleteModal = () => setDeleteModal(false);
  const handleOpenDeleteModal = (id) => {
    setSelectedId(id);
    setDeleteModal(true);
  };

  const { columns, rows } = useDailyShiftData({
    handleOpenDeleteModal,
    shiftList: shiftData?.shiftList,
  });

  const handleErrorFunc = (message = Constants.SOMETHING_WENT_WRONG) => {
    dispatch(
      openSnackbar({
        message,
        notificationType: Constants.NOTIFICATION_ERROR,
      })
    );
  };

  // Func to get Shift Details list
  const getShiftDetailsDataFunc = async (temp = filters, currentPage = 0) => {
    setTablePagination({ ...tablePagination, page: currentPage });
    const paramData = {
      page: currentPage,
      perPage: tablePagination.perPage,
      project: temp[0].selectedValue,
      date:
        temp[1].selectedValue === Common.CUSTOM_RANGE
          ? Common.CUSTOM_VALUE
          : temp[1].selectedValue.replace(/\s+/g, "_").toLowerCase(),
      status: temp[2].selectedValue.replace(/\s+/g, "_").toLowerCase(),
    };

    // Add date parameters if Custom Range is selected
    if (temp[1].selectedValue === Common.CUSTOM_RANGE) {
      paramData.fromDate = temp[1].fromDate;
      paramData.toDate = temp[1].toDate;

      // Skip API call if Custom Range is selected but either date is missing
      if (!paramData.fromDate || !paramData.toDate) {
        return;
      }
    }

    const params = paramCreater(paramData);
    await dispatch(dalShitFiltersThunk(params));
  };

  const handleTablePagination = async (value) => {
    // check alrady fetched data
    if (shiftData.shiftListingPagiantion.completed.includes(value)) return;
    const queryParams = {
      page: value,
      perPage: tablePagination.perPage,
      project: filters[0].selectedValue,
      date:
        filters[1].selectedValue === Common.CUSTOM_RANGE
          ? Common.CUSTOM_VALUE
          : filters[1].selectedValue.toLowerCase().replace(/ /g, "_"),
      status: filters[2].selectedValue.toLowerCase().replace(/ /g, "_"),
    };

    // Add date parameters if Custom Range is selected
    if (filters[1].selectedValue === Common.CUSTOM_RANGE) {
      queryParams.fromDate = filters[1].fromDate;
      queryParams.toDate = filters[1].toDate;

      // Skip API call if Custom Range is selected but either date is missing
      if (!queryParams.fromDate || !queryParams.toDate) {
        return;
      }
    }

    const data = new URLSearchParams(queryParams);
    await dispatch(dalShitFiltersThunk(data));
  };

  const handleDeleteShift = async () => {
    const res = await dispatch(deleteShiftThunk(selectedId));

    if (res.payload.status === 200) {
      await dispatch(removeShift(selectedId));
      const { page, perPage } = tablePagination;
      const { totalRecords } = shiftData.shiftListingPagiantion;

      const data = {
        page: findPageNumberAfterRecordDelete(page, perPage, totalRecords),
        perPage: tablePagination.perPage,
        project: filters[0].selectedValue,
        date:
          filters[1].selectedValue === Common.CUSTOM_RANGE
            ? Common.CUSTOM_VALUE
            : filters[1].selectedValue.toLowerCase().replace(/ /g, "_"),
        status: filters[2].selectedValue.toLowerCase().replace(/ /g, "_"),
      };

      // Add date parameters if Custom Range is selected
      if (filters[1].selectedValue === Common.CUSTOM_RANGE) {
        data.fromDate = filters[1].fromDate;
        data.toDate = filters[1].toDate;

        // Skip API call if Custom Range is selected but either date is missing
        if (!data.fromDate || !data.toDate) {
          return;
        }
      }
      const queryParams = new URLSearchParams(data);
      const shiftRes = await dispatch(dalShitFiltersThunk(queryParams));
      if (shiftRes.payload.status === Common.API_STATUS_200) {
        const completedPages = [...shiftData.shiftListingPagiantion.completed];
        for (let i = completedPages.length - 1; i >= 0; i -= 1) {
          if (completedPages[i] > tablePagination.page) {
            completedPages.splice(i, 1);
          }
        }
        await dispatch(updateCompleted(completedPages));
        dispatch(
          openSnackbar({
            message: Constants.SHIFT_DELETE_SUCCESS,
            notificationType: Constants.NOTIFICATION_SUCCESS,
          })
        );
      }
    } else if (res.payload.status === Common.API_STATUS_400) {
      dispatch(
        openSnackbar({
          message: Constants.INVALID_SHIFT,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
    } else if (res.payload.status === Common.API_STATUS_500) {
      handleErrorFunc();
    }
    setDeleteModal(false);
  };

  // Get Project List Func for dropdown.
  const getProjectListFunc = async () => {
    try {
      const projectList = await dispatch(projectListThunk());
      if (projectList?.payload?.status) {
        setFilters((prev) => {
          const updatedFilters = prev.map((filter) => {
            if (filter.inputLabel === FiltersModuleName.PROJECT) {
              return {
                ...filter,
                selectedValue: projectList?.payload?.data?.some(
                  (project) => project[mongooseId] === filters[0]?.selectedValue
                )
                  ? filters[0]?.selectedValue
                  : FiltersModuleName.ALL_IN_SMALL_CASE,

                list: [
                  FiltersModuleName.SHIFT_DETAILS_FILTERS_TITLE_OBJ,
                  ...projectList.payload.data,
                ],
              };
            }
            return filter;
          });

          return updatedFilters;
        });
        setShouldUpdateState((prev) => !prev);
      }
    } catch (error) {
      handleErrorFunc();
    }
  };

  // get User List Func
  const getUserListFunc = async () => {
    const params = paramCreater({ isActive: true });
    try {
      const res = await dispatch(UserListThunk(params));
      if (res?.payload?.status) {
        const userList = res.payload.data.data.usersData || [];
        const filteredUserList = userList.filter(
          (user) =>
            user.role?.title.toLowerCase() !== defaultData.ADMIN_ROLE &&
            user.role?.title.toLowerCase() !== defaultData.SUPER_ADMIN_ROLE
        );

        setActiveUsers(filteredUserList);
      }
    } catch (error) {
      handleErrorFunc();
    }
  };

  useEffect(() => {
    getProjectListFunc();
    getUserListFunc();
  }, []);

  useEffect(() => {
    getShiftDetailsDataFunc();
  }, [shouldUpdateState]);

  const handleCustomDateChange = (field, value) => {
    setFilters((prevFilters) => {
      const updatedFilters = prevFilters.map((filter) => {
        if (filter.inputLabel === FiltersModuleName.CREATED) {
          return { ...filter, [field]: value };
        }
        return filter;
      });

      // Get the updated Created filter
      const createdFilter = updatedFilters.find(
        (filter) => filter.inputLabel === FiltersModuleName.CREATED
      );

      // Only trigger filter if both dates are selected and Custom Range is selected
      if (
        createdFilter &&
        createdFilter.selectedValue === Common.CUSTOM_RANGE &&
        createdFilter.fromDate &&
        createdFilter.toDate
      ) {
        dispatch(setStoreFilters({ module: "shift", filters: updatedFilters }));
        setShouldUpdateState((prev) => !prev);
      } else {
        dispatch(setStoreFilters({ module: "shift", filters: updatedFilters }));
      }

      return updatedFilters;
    });
  };

  const handleFilterType = async (e) => {
    const { name, value } = e.target;

    setFilters((prev) => {
      const updatedFilters = prev.map((filter) => {
        if (filter.inputLabel === name) {
          return {
            ...filter,
            selectedValue: value,
          };
        }
        return filter;
      });

      // Updating the Store also with the latest value.
      dispatch(setStoreFilters({ module: "shift", filters: updatedFilters }));
      return updatedFilters;
    });

    // Skip API call if Custom Range is selected but dates are not set
    const createdFilter = filters.find((filter) => filter.inputLabel === FiltersModuleName.CREATED);
    if (
      name === FiltersModuleName.CREATED &&
      value === Common.CUSTOM_RANGE &&
      (!createdFilter.fromDate || !createdFilter.toDate)
    ) {
      return;
    }

    setShouldUpdateState((prev) => !prev);
  };

  const handleReset = async () => {
    setFilters((prevFilters) => {
      const updatedFilters = prevFilters.map((filter) => {
        // For date filter, also reset fromDate and toDate
        if (filter.inputLabel === FiltersModuleName.CREATED) {
          return {
            ...filter,
            selectedValue: filter.list[0],
            fromDate: "",
            toDate: "",
          };
        }
        // For other filters, reset to their first option
        return {
          ...filter,
          selectedValue: filter.list[0][mongooseId] || filter.list[0],
        };
      });
      return updatedFilters;
    });

    dispatch(resetFilters({ module: "shift" }));
    setShouldUpdateState((prev) => !prev);
  };

  const handleReload = async () => {
    await dispatch(reloadData());
    await dispatch(configThunk());
    await getProjectListFunc();

    setShouldUpdateState((prev) => !prev);
  };

  const handleExport = async (format) => {
    setExportLoader(true);

    const filename = `shifts.${format}`;
    const queryParams = {
      project: filters[0].selectedValue,
      date:
        filters[1].selectedValue === Common.CUSTOM_RANGE
          ? Common.CUSTOM_VALUE
          : filters[1].selectedValue.toLowerCase().replace(/ /g, "_"),
      status: filters[2].selectedValue.toLowerCase().replace(/ /g, "_"),
    };

    if (filters[1].selectedValue === Common.CUSTOM_RANGE) {
      queryParams.fromDate = filters[1].fromDate;
      queryParams.toDate = filters[1].toDate;

      if (!queryParams.fromDate || !queryParams.toDate) {
        setExportLoader(false);
        return;
      }
    }

    dispatch(
      openSnackbar({
        message: (
          <MDBox display="flex" alignItems="center">
            {Icons.LOADING}
            <MDTypography
              variant="button"
              color="light"
              ml={2}
              fontWeight="regular"
              sx={{ fontSize: "14px" }}
            >
              {Constants.EXCEL_PREPARING}
            </MDTypography>
          </MDBox>
        ),
        notificationType: "info",
      })
    );

    const queryString = new URLSearchParams(queryParams).toString();
    const res = await dispatch(exportAllShiftPdf(queryString));

    if (res.error || !res.payload?.data?.jobId) {
      handleErrorFunc(res.error ? res.payload?.message : undefined);
      setExportLoader(false);
      return;
    }

    const { jobId, socketUrl, statusUrl, downloadUrl, fileName } = res.payload.data;

    try {
      const socket = createSocketClient(socketUrl);
      let settled = false;
      let timeoutId = null;
      let fallbackInProgress = false;
      let completionReceived = false;
      const getStatusUrl = () =>
        statusUrl || `${socketUrl.replace(/\/$/, "")}/api/excel/excel-status/${jobId}`;
      const getDownloadUrl = (data = null) =>
        data?.downloadUrl ||
        downloadUrl ||
        `${socketUrl.replace(/\/$/, "")}/api/excel/excel-download/${jobId}`;

      const settle = (options = {}) => {
        if (settled) return;
        settled = true;
        if (timeoutId) clearTimeout(timeoutId);
        disconnectSocket(socket);
        if (options.message) {
          dispatch(
            openSnackbar({
              message: options.message,
              notificationType: options.type || Constants.NOTIFICATION_ERROR,
            })
          );
        }
        setExportLoader(false);
      };

      const fetchStatusPayload = () => fetchExportStatusPayload(getStatusUrl());

      const pollExcelUntilReady = () =>
        pollExportJobStatusUntilReady(getStatusUrl(), {
          failedMessage: Constants.EXCEL_GENERATION_FAILED,
          timeoutMessage: Constants.EXCEL_EXPORT_TIMED_OUT,
          timeoutMs: EXCEL_SOCKET_TIMEOUT_MS,
          intervalMs: EXCEL_STATUS_POLL_INTERVAL_MS,
        });

      const completeDownload = async (data = null) => {
        completionReceived = true;
        if (timeoutId) clearTimeout(timeoutId);
        disconnectSocket(socket);

        if (data && !data.success) {
          settle({ message: Constants.EXCEL_GENERATION_FAILED });
          return;
        }

        if (data?.emailed || data?.emailAttempted) {
          settle({
            message: Constants.EXCEL_EMAIL_SENT_HEAVY_LOAD,
            type: Constants.NOTIFICATION_SUCCESS,
          });
          return;
        }

        try {
          const excelBytes = await fetchBytesFromUrl(
            getDownloadUrl(data),
            Constants.EXCEL_GENERATION_FAILED
          );
          await triggerFileDownload(
            excelBytes,
            data?.fileName || fileName || filename,
            EXCEL_CONTENT_TYPE
          );
          dispatch(
            openSnackbar({
              message: Constants.EXCEL_DOWNLOAD_COMPLETE,
              notificationType: Constants.NOTIFICATION_SUCCESS,
            })
          );
          settle({ type: Constants.NOTIFICATION_SUCCESS });
        } catch (_err) {
          settle({ message: Constants.EXCEL_GENERATION_FAILED });
        }
      };

      let pollingStarted = false;
      const startPollingWatcher = async () => {
        pollingStarted = true;
        try {
          const readyData = await pollExcelUntilReady();
          if (settled || completionReceived) return;
          await completeDownload(readyData);
        } catch (_error) {
          // Socket completion or timeout fallback remains active.
        }
      };

      const settleWithFallback = async (message = Constants.EXCEL_GENERATION_FAILED) => {
        if (settled || fallbackInProgress) return;
        fallbackInProgress = true;

        try {
          const readyData = await pollExcelUntilReady();
          if (settled) return;
          await completeDownload(readyData);
        } catch (_error) {
          settle({ message });
        } finally {
          fallbackInProgress = false;
        }
      };

      timeoutId = setTimeout(() => {
        settleWithFallback(Constants.EXCEL_EXPORT_TIMED_OUT);
      }, EXCEL_SOCKET_TIMEOUT_MS);

      startPollingWatcher();

      socket.on(`job_progress_${jobId}`, (progress) => {
        dispatch(
          openSnackbar({
            message: (
              <MDBox display="flex" alignItems="center">
                {Icons.LOADING}
                <MDTypography
                  variant="button"
                  color="light"
                  ml={2}
                  fontWeight="regular"
                  sx={{ fontSize: "14px" }}
                >
                  {progress.message}...
                </MDTypography>
              </MDBox>
            ),
            notificationType: "info",
          })
        );
      });

      socket.on("connect", async () => {
        // Skip immediate fetch — startPollingWatcher already checks on its first iteration.
        if (settled || completionReceived || pollingStarted) return;
        try {
          const payload = await fetchStatusPayload();
          if (
            payload &&
            (payload.status === "completed" || payload.status === "emailed") &&
            payload.resultUrl &&
            !settled &&
            !completionReceived
          ) {
            await completeDownload(payload);
          }
        } catch (_err) {
          if (!settled) {
            settle({ message: Constants.EXCEL_GENERATION_FAILED });
          }
        }
      });

      socket.on(`job_completed_${jobId}`, async (data) => {
        if (settled) return;
        completionReceived = true;
        if (timeoutId) clearTimeout(timeoutId);
        disconnectSocket(socket);
        await completeDownload(data);
      });

      socket.on("connect_error", () => {
        settleWithFallback(Constants.EXCEL_CONNECTION_FAILED);
      });

      socket.on("connect_timeout", () => {
        settleWithFallback(Constants.EXCEL_CONNECTION_FAILED);
      });

      socket.on("disconnect", () => {
        if (!settled && !completionReceived) {
          settleWithFallback(Constants.EXCEL_GENERATION_FAILED);
        }
      });
    } catch (_err) {
      dispatch(
        openSnackbar({
          message: Constants.SOMETHING_WENT_WRONG,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
      setExportLoader(false);
    }
  };

  return (
    <DashboardLayout module={defaultData.SHIFT_SCREEN_ID}>
      <DashboardNavbar />
      <MDBox
        display="flex"
        flexDirection={{ md: "row", sm: "column" }}
        justifyContent={{ md: "space-between" }}
        alignItems={{ lg: "space-between", sm: "center" }}
      >
        <PageTitle title={PageTitles.DAL_SHIFT} />
        <MDBox mt={{ lg: 0, sm: 2 }} display="flex" flexWrap="wrap">
          {permission?.create && (
            <CustomButton
              title={ButtonTitles.NEW_SHIFT}
              icon={Icons.NEW}
              background={Colors.PRIMARY}
              color={Colors.WHITE}
              openModal={setOpenNewShift}
            />
          )}
          <Divider
            orientation="vertical"
            sx={{
              backgroundColor: "var(--gray-300, #D0D5DD)",
              height: "auto",
              marginLeft: pxToRem(16),
              marginRight: 0,
            }}
          />
          <BasicButton
            icon={Icons.RELOAD}
            background={Colors.WHITE}
            border
            color={Colors.BLACK}
            action={handleReload}
          />
        </MDBox>
      </MDBox>
      <Divider sx={{ marginTop: pxToRem(22) }} />
      <Feature name={FeatureTags.SHIFT_DETAILS}>
        <MDBox display="flex" justifyContent="space-between" mx={0}>
          <MDBox
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "start",
              alignItems: "flex-end",
              mx: 0,
              rowGap: pxToRem(16),
            }}
          >
            {filters?.map((val) => {
              const isProject = val?.inputLabel === FiltersModuleName.PROJECT;
              return isProject ? (
                <FormControl
                  key={val?.inputLabel}
                  variant="standard"
                  size="medium"
                  style={{ marginTop: "26px", width: 200, marginRight: "15px" }}
                >
                  <CustomAutoComplete
                    label={val?.inputLabel}
                    name={val?.inputLabel}
                    id={val?.inputLabel}
                    getOptionLabel={(option) => option.title || ""}
                    menu={val?.list}
                    value={{
                      title:
                        val?.list.find((item) => item[Constants.MONGOOSE_ID] === val?.selectedValue)
                          ?.title || "",
                    }}
                    handleChange={handleFilterType}
                    valueStyle={{
                      backgroundColor: Colors.WHITE,
                      height: pxToRem(40),
                      verticalMarginTop: pxToRem(4),
                      menuWidth: 400,
                      inputWidth: 250,
                      padding: pxToRem(1),
                    }}
                    labelStyle={{
                      fontSize: pxToRem(14),
                      fontWeight: 600,
                      color: Colors.BLACK,
                    }}
                  />
                </FormControl>
              ) : (
                <CustomFilterDropdown
                  key={val.inputLabel}
                  label={val.inputLabel}
                  name={val.inputLabel}
                  defaultValue={val?.selectedValue}
                  value={val?.selectedValue}
                  handleChange={handleFilterType}
                  menu={val.list}
                  maxContent={defaultData.MEDIUM_CONTENT_LENGTH}
                  fromDate={val.fromDate}
                  toDate={val.toDate}
                  handleCustomDateChange={handleCustomDateChange}
                />
              );
            })}
            <MDBox sx={{ marginRight: pxToRem(8) }}>
              <Button
                sx={{
                  mr: 1,
                  backgroundColor: "#fff",
                  "&:hover": {
                    backgroundColor: "#fff",
                  },
                  fontSize,
                  height: pxToRem(40),
                  textTransform: "capitalize",
                }}
                variant="outlined"
                color="info"
                onClick={handleReset}
                startIcon={Icons.RESET_FILTER}
              >
                {ButtonTitles.RESET_FILTER}
              </Button>
            </MDBox>
            <BasicButton
              title={exportLoader ? ButtonTitles.EXPORTING : ButtonTitles.EXPORT}
              icon={Icons.EXPORT}
              background={Colors.WHITE}
              color={Colors.BLACK}
              border
              action={() => handleExport("xlsx")}
              disabled={exportLoader || rows.length === 0}
              style={{ btnMarginLeft: pxToRem(0) }}
            />
          </MDBox>
        </MDBox>
        <MDBox mt={3} mb={3}>
          <Grid item xs={12}>
            <Card sx={{ boxShadow: "none" }}>
              <MDBox>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={{ defaultValue: defaultData.PER_PAGE_5 }}
                  showTotalEntries={false}
                  noEndBorder
                  loading={shiftData.loading}
                  licenseRequired
                  currentPage={tablePagination.page}
                  handleTablePagination={handleTablePagination}
                  handleCurrentPage={(page) => setTablePagination({ ...tablePagination, page })}
                  isGotoVisisble
                />
              </MDBox>
            </Card>
          </Grid>
        </MDBox>

        {/* New Shift */}
        {openNewShift && (
          <NewShift
            openShiftModal={openNewShift}
            setOpenShiftModal={setOpenNewShift}
            setShouldUpdateState={setShouldUpdateState}
            activeUsers={activeUsers}
          />
        )}

        {openDeleteModal && (
          <DeleteModal
            open={openDeleteModal}
            title={ModalContent.SHIFT_DELETE_TITLE}
            message={ModalContent.DELETE_SHIFT}
            handleClose={handleCloseDeleteModal}
            handleDelete={handleDeleteShift}
          />
        )}
      </Feature>
    </DashboardLayout>
  );
}

export default DalShiftDetails;
